// XSM-1536 - SSO Login flow
// Author: Abhishek Bharti
import { useEffect, useState } from "react";
import {
  USER_DOMAIN,
  XSM_BASE_URL,
  XSM_ORIGIN,
  MASTER_CALLBACK_URL,
  PARTNER_CALLBACK_URL,
  SYS_ADMIN_CALLBACK_URL,
  XSM_MASTER_SUB_DOMAIN,
  XSM_PARTNER_SUB_DOMAIN,
  XSM_SYSTEM_SUB_DOMAIN,
} from "src/constants/auth";

import Integrations from "../assets/images/App_Registry.png";
import Partner from "../assets/images/Access_Manager.png";
import SystemAdmin from "../assets/images/EVES_Ops.png";
const ORGANISATION_ENDPOINT = "/xsm-api/v1/organization";
const USER_ENDPOINT = "/xsm-api/v1/user";

type Organisation = {
  id?: string | number;
  name?: string;
};

type OrganisationApiItem = {
  org_uuid?: string | number;
  org_name?: string;
};

export type ProductListItem = {
  id: string | number;
  name: string;
  icon: string;
  link: string;
};

type OrganisationApiResponse = {
  result?: {
    organizations?: OrganisationApiItem[];
    organisation?: OrganisationApiItem[];
  };
  organizations?: OrganisationApiItem[];
  organisation?: OrganisationApiItem[];
};

type UserDetailsApiOrganization = {
  name: string;
  id: string;
  application: unknown[];
};

type UserDetailsApiResponse = {
  statusCode?: number;
  isSuccess?: boolean;
  message?: string;
  totalRecords?: number;
  errorMessages?: unknown[];
  result?: {
    users?: Array<{
      organizations?: UserDetailsApiOrganization[];
      [key: string]: unknown;
    }>;
  };
};

const SUBDOMAIN_ORG_EXCLUSION_MAP: Record<string, string> = {
  [XSM_MASTER_SUB_DOMAIN]: "XSM-Master",
  [XSM_PARTNER_SUB_DOMAIN]: "Partner",
  [XSM_SYSTEM_SUB_DOMAIN]: "EvesSysAdmin",
};

const ORGANISATION_PRODUCT_CONFIG: Record<
  string,
  { displayName: string; icon: string; link: () => string }
> = {
  "XSM-Master": {
    displayName: "App Registry",
    icon: Integrations,
    link: () => MASTER_CALLBACK_URL,
  },
  Partner: {
    displayName: "Access Manager",
    icon: Partner,
    link: () => PARTNER_CALLBACK_URL,
  },
  EvesSysAdmin: {
    displayName: "EVES Ops",
    icon: SystemAdmin,
    link: () => SYS_ADMIN_CALLBACK_URL,
  },
};

const getSubdomain = () => {
  if (typeof window === "undefined") {
    return "";
  }

  const hostname = window.location.hostname;

  if (!hostname || hostname === "localhost") {
    return "";
  }

  const parts = hostname.split(".");
  if (parts.length < 3) {
    return parts[0] ?? "";
  }

  return parts[0] ?? "";
};

export const productListMapping = (
  organisations: Organisation[]
): ProductListItem[] => {
  if (!(organisations instanceof Array)) {
    return [];
  }

  const subdomain = getSubdomain() || "";
  const excludedOrganisation = SUBDOMAIN_ORG_EXCLUSION_MAP[subdomain];

  // Always exclude SYSTEM_ORG and DeviceManager
  const excludedOrganisations = ["SYSTEM_ORG", "DeviceManager"];

  return organisations
    .filter(
      (organisation) =>
        organisation?.name && !excludedOrganisations.includes(organisation.name)
    )
    .filter(
      (organisation) =>
        !excludedOrganisation || organisation?.name !== excludedOrganisation
    )
    .map<ProductListItem | null>((organisation, index) => {
      const config = organisation?.name
        ? ORGANISATION_PRODUCT_CONFIG[organisation.name]
        : undefined;
      if (!config) {
        return null;
      }

      return {
        id: organisation?.id ?? index,
        name: config.displayName,
        icon: config.icon,
        link: config.link(),
      };
    })
    .filter((item): item is ProductListItem => Boolean(item));
};

export const fetchUserDetails = async (): Promise<Organisation[]> => {
  const token = localStorage.getItem("dm_access_token");
  const userName = localStorage.getItem("dm_user_name");
  if (!token || !userName) {
    return [];
  }

  const url = new URL(`${XSM_BASE_URL}${USER_ENDPOINT}`);
  url.searchParams.set("userName", userName);
  url.searchParams.set("domainName", USER_DOMAIN);

  const userResponse = await fetch(url.toString(), {
    method: "GET",
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
      "XSM-Domain": USER_DOMAIN,
      "XSM-Internal": "true",
      "X-Original-Origin": XSM_ORIGIN,
    },
  });

  if (!userResponse.ok) {
    const errorBody = await userResponse.text().catch(() => "");
    throw new Error(
      `Failed to fetch user details: ${userResponse.status} ${errorBody}`
    );
  }

  const response: UserDetailsApiResponse = await userResponse.json();
  const userData = response?.result?.users?.[0];
  const organizationData = userData?.organizations || [];

  if (!Array.isArray(organizationData)) {
    return [];
  }

  // Map organizations from user details API to Organisation format
  // The API already provides 'name' and 'id' fields, so we can use them directly
  return organizationData.map<Organisation>((org) => ({
    id: org?.id,
    name: org?.name,
  }));
};

export const fetchOrganizations = async () => {
  const token = localStorage.getItem("dm_access_token");
  if (!token) {
    return [];
  }

  const url = new URL(`${XSM_BASE_URL}${ORGANISATION_ENDPOINT}`);
  url.searchParams.set("domainName", USER_DOMAIN);

  const response = await fetch(url.toString(), {
    method: "GET",
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
      "XSM-Domain": USER_DOMAIN,
      "xsm-internal": "true",
      "X-Original-Origin": XSM_ORIGIN,
    },
  });

  if (!response.ok) {
    const errorBody = await response.text().catch(() => "");
    throw new Error(
      `Failed to fetch organisations: ${response.status} ${errorBody}`
    );
  }

  const data: OrganisationApiResponse = await response.json();
  const organisations = data?.result?.organizations ?? [];

  if (!Array.isArray(organisations)) {
    return [];
  }

  return organisations.map<Organisation>((organisation, index) => ({
    id: organisation?.org_uuid ?? index,
    name: organisation?.org_name,
  }));
};

export const useProductList = () => {
  const [products, setProducts] = useState<ProductListItem[]>([]);

  useEffect(() => {
    let isMounted = true;

    const loadOrganisations = async () => {
      try {
        const organisations = await fetchUserDetails();
        const mappedProducts = productListMapping(organisations);
        if (isMounted) {
          setProducts(mappedProducts);
        }
      } catch (error) {
        console.error("Failed to fetch organisations", error);
        if (isMounted) {
          setProducts([]);
        }
      }
    };

    loadOrganisations();

    return () => {
      isMounted = false;
    };
  }, []);

  return { products };
};
